/**
 * 연차 계산 유틸리티 함수
 * 입사일 기준 개별 연차 관리를 위한 핵심 계산 로직
 */

import {
  AnnualLeaveBalance,
  LeaveType,
  HalfDayType,
  LeaveSettings,
  ValidationResult,
  LeaveCalculationParams,
  LeaveCalculationResult,
} from '../../types/annualLeave';
import { AppError, ErrorCodes } from '../errorHandler.ts';

// ===== 날짜 관련 유틸리티 =====

/**
 * 날짜를 UTC 기준으로 정규화
 * 시간대 문제 해결을 위해 모든 날짜를 UTC 00:00:00으로 설정
 */
const normalizeDate = (date: Date): Date => {
  const normalized = new Date(date);
  normalized.setUTCHours(0, 0, 0, 0);
  return normalized;
};

/**
 * 날짜 문자열 또는 Date 객체를 Date 객체로 변환 (YYYY-MM-DD 형식)
 * Date 객체가 입력되면 정규화하여 반환, 문자열이면 파싱하여 반환
 */
export const parseDate = (dateInput: string | Date): Date => {
  // 이미 Date 객체인 경우 정규화하여 반환
  if (dateInput instanceof Date) {
    return normalizeDate(dateInput);
  }

  // 문자열인 경우 기존 로직 적용
  if (typeof dateInput === 'string') {
    // 빈 문자열 체크
    if (!dateInput || !dateInput.trim()) {
      throw new AppError(
        'Date string cannot be empty',
        ErrorCodes.VALIDATION_ERROR,
        { receivedValue: dateInput }
      );
    }

    // YYYY-MM-DD 형식 검증
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateInput)) {
      throw new AppError(
        `Invalid date format: expected YYYY-MM-DD, got ${dateInput}`,
        ErrorCodes.VALIDATION_ERROR,
        { receivedValue: dateInput }
      );
    }

    const [year, month, day] = dateInput.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day));
  }

  // 예상치 못한 타입인 경우 에러
  throw new AppError(
    `Invalid date input type: expected string or Date, got ${typeof dateInput}`,
    ErrorCodes.VALIDATION_ERROR,
    { receivedType: typeof dateInput }
  );
};

/**
 * Date 객체를 YYYY-MM-DD 형식 문자열로 변환
 */
export const formatDate = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * YYYY-MM 형식 문자열 반환
 */
export const formatYearMonth = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

// ===== 근속 기간 계산 =====

/**
 * 근속년수 계산 (소수점 포함)
 * 입사일부터 체크일까지의 정확한 근속 기간 계산
 *
 * @param hireDate 입사일
 * @param checkDate 체크일 (기본값: 오늘)
 * @returns 근속년수 (예: 1.5 = 1년 6개월)
 * @throws 입사일이 체크일보다 미래인 경우 에러
 */
export const calculateSeniorityYears = (
  hireDate: Date,
  checkDate: Date = new Date()
): number => {
  const normalizedHireDate = normalizeDate(hireDate);
  const normalizedCheckDate = normalizeDate(checkDate);

  // 입사일이 체크일보다 미래인 경우 에러
  if (normalizedHireDate > normalizedCheckDate) {
    throw new AppError(
      '입사일이 체크일보다 미래일 수 없습니다.',
      ErrorCodes.VALIDATION_ERROR,
      { hireDate: normalizedHireDate, checkDate: normalizedCheckDate },
      true
    );
  }

  // 일수 차이 계산
  const diffTime = normalizedCheckDate.getTime() - normalizedHireDate.getTime();
  const diffDays = diffTime / (1000 * 60 * 60 * 24);

  // 365.25일 (윤년 고려)로 나누어 년수 계산
  const years = diffDays / 365.25;

  // 소수점 둘째 자리까지 반올림
  return Math.round(years * 100) / 100;
};

/**
 * 근속 월수 계산 (1년 미만 직원용)
 *
 * @param hireDate 입사일
 * @param checkDate 체크일
 * @returns 근속 월수 (최대 11)
 */
export const calculateSeniorityMonths = (
  hireDate: Date,
  checkDate: Date = new Date()
): number => {
  const normalizedHireDate = normalizeDate(hireDate);
  const normalizedCheckDate = normalizeDate(checkDate);

  let months = 0;
  const tempDate = new Date(normalizedHireDate);

  // 먼저 1개월 기념일로 이동 (입사일 당일은 0개월)
  tempDate.setMonth(tempDate.getMonth() + 1);

  // 기념일이 지났는지 확인하며 개월 수 카운트
  while (tempDate <= normalizedCheckDate) {
    months++;
    tempDate.setMonth(tempDate.getMonth() + 1);

    // 최대 11개월
    if (months >= 11) break;
  }

  return Math.min(months, 11);
};

// ===== 연차 일수 계산 =====

/**
 * 총 연차 일수 계산
 * 사업장 규모와 근속년수를 고려한 연차 일수 산정
 *
 * @param seniorityYears 근속년수
 * @param isLargeWorkplace 5인 이상 사업장 여부
 * @param settings 연차 설정 (선택적)
 * @returns 총 연차 일수
 */
export const calculateTotalLeaveDays = (
  seniorityYears: number,
  isLargeWorkplace: boolean,
  settings?: LeaveSettings
): number => {
  // 1년 미만은 월차 대상
  if (seniorityYears < 1) {
    return 0;
  }

  // 기본 연차 일수
  let baseDays: number;
  if (settings) {
    baseDays = isLargeWorkplace
      ? settings.baseLeaveDays.largeWorkplace
      : settings.baseLeaveDays.smallWorkplace;
  } else {
    // 기본값: 5인 이상 15일, 5인 미만 12일
    baseDays = isLargeWorkplace ? 15 : 12;
  }

  // 근속 보너스 계산 (5인 이상 사업장만)
  let bonusDays = 0;
  if (isLargeWorkplace) {
    // 3년차부터 2년마다 1일씩 추가 (최대 25일)
    if (seniorityYears >= 3) {
      bonusDays = Math.floor((seniorityYears - 1) / 2);

      // 설정이 있고 보너스가 활성화된 경우
      if (settings?.seniorityBonusDays?.enabled) {
        bonusDays = Math.min(bonusDays, settings.seniorityBonusDays.maxBonusDays);
      }
    }
  }

  // 최대 25일 제한 (5인 이상 사업장)
  const totalDays = baseDays + bonusDays;
  return isLargeWorkplace ? Math.min(totalDays, 25) : totalDays;
};

/**
 * 월차 일수 계산 (1년 미만 직원)
 *
 * @param seniorityMonths 근속 월수
 * @returns 월차 일수 (최대 11일)
 */
export const calculateMonthlyLeaveDays = (seniorityMonths: number): number => {
  return Math.min(seniorityMonths, 11);
};

// ===== 입사 기념일 관련 =====

/**
 * 오늘이 입사 기념일인지 확인
 *
 * @param hireDate 입사일
 * @param checkDate 체크일 (기본값: 오늘)
 * @returns 입사 기념일 여부
 */
export const isAnniversaryToday = (
  hireDate: Date,
  checkDate: Date = new Date()
): boolean => {
  const normalizedHireDate = normalizeDate(hireDate);
  const normalizedCheckDate = normalizeDate(checkDate);

  // 월과 일이 같은지 확인
  return (
    normalizedHireDate.getUTCMonth() === normalizedCheckDate.getUTCMonth() &&
    normalizedHireDate.getUTCDate() === normalizedCheckDate.getUTCDate()
  );
};

/**
 * 다음 입사 기념일 계산
 *
 * @param hireDate 입사일
 * @param checkDate 체크일
 * @returns 다음 입사 기념일
 */
export const getNextAnniversary = (
  hireDate: Date,
  checkDate: Date = new Date()
): Date => {
  const normalizedHireDate = normalizeDate(hireDate);
  const normalizedCheckDate = normalizeDate(checkDate);

  const nextAnniversary = new Date(normalizedHireDate);
  nextAnniversary.setUTCFullYear(normalizedCheckDate.getUTCFullYear());

  // 이미 지난 경우 내년으로
  if (nextAnniversary < normalizedCheckDate) {
    nextAnniversary.setUTCFullYear(nextAnniversary.getUTCFullYear() + 1);
  }

  return nextAnniversary;
};

/**
 * 입사 기념일 연도-월 반환
 *
 * @param anniversaryDate 입사 기념일
 * @returns YYYY-MM 형식
 */
export const getAnniversaryYearMonth = (anniversaryDate: Date): string => {
  return formatYearMonth(anniversaryDate);
};

// ===== 유효기간 계산 =====

/**
 * 연차 유효기간 계산
 * 입사 기념일부터 1년간 유효
 *
 * @param anniversaryDate 입사 기념일
 * @returns 유효기간 시작일과 종료일
 */
export const calculateValidPeriod = (
  anniversaryDate: Date
): { validFrom: Date; validUntil: Date } => {
  const validFrom = normalizeDate(anniversaryDate);

  // 1년 후 - 1일
  const validUntil = new Date(validFrom);
  validUntil.setUTCFullYear(validUntil.getUTCFullYear() + 1);
  validUntil.setUTCDate(validUntil.getUTCDate() - 1);

  return { validFrom, validUntil };
};

/**
 * 날짜가 유효기간 내에 있는지 확인
 *
 * @param date 확인할 날짜
 * @param validFrom 유효기간 시작일
 * @param validUntil 유효기간 종료일
 * @returns 유효기간 내 여부
 */
export const isDateInValidPeriod = (
  date: Date,
  validFrom: Date,
  validUntil: Date
): boolean => {
  const normalizedDate = normalizeDate(date);
  const normalizedFrom = normalizeDate(validFrom);
  const normalizedUntil = normalizeDate(validUntil);

  return normalizedDate >= normalizedFrom && normalizedDate <= normalizedUntil;
};

// ===== 차감 일수 계산 =====

/**
 * 근무일 수 계산 (주말 제외)
 *
 * @param startDate 시작일
 * @param endDate 종료일
 * @returns 근무일 수
 */
export const calculateWorkingDays = (
  startDate: Date,
  endDate: Date
): number => {
  const start = normalizeDate(startDate);
  const end = normalizeDate(endDate);

  let workingDays = 0;
  const current = new Date(start);

  while (current <= end) {
    const dayOfWeek = current.getUTCDay();
    // 토요일(6), 일요일(0) 제외
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      workingDays++;
    }
    current.setUTCDate(current.getUTCDate() + 1);
  }

  return workingDays;
};

/**
 * 연차 차감 일수 계산
 * 연차 종류에 따라 차감될 일수 계산
 *
 * @param leaveType 연차 종류
 * @param startDate 시작일
 * @param endDate 종료일
 * @param hours 시간 (시간차인 경우)
 * @param halfDayType 반차 종류
 * @param settings 연차 설정
 * @returns 차감 일수
 */
export const calculateDeductionDays = (
  leaveType: LeaveType,
  startDate: Date,
  endDate: Date,
  hours?: number,
  halfDayType?: HalfDayType,
  settings?: LeaveSettings
): number => {
  switch (leaveType) {
    case 'full':
      // 전일: 근무일 수
      return calculateWorkingDays(startDate, endDate);

    case 'half':
      // 반차: 오전 0.4375일 (3.5시간), 오후 0.5625일 (4.5시간)
      if (!halfDayType) {
        throw new AppError(
          '반차 종류를 선택해야 합니다.',
          ErrorCodes.VALIDATION_ERROR,
          { leaveType, halfDayType },
          true
        );
      }

      if (settings?.halfDayHours) {
        const hours = halfDayType === 'morning'
          ? settings.halfDayHours.morning
          : settings.halfDayHours.afternoon;
        return hours / 8; // 8시간 기준
      }

      // 기본값
      return halfDayType === 'morning' ? 0.4375 : 0.5625;

    case 'hourly':
      // 시간차: 시간 / 8
      if (!hours || hours <= 0) {
        throw new AppError(
          '시간을 입력해야 합니다.',
          ErrorCodes.VALIDATION_ERROR,
          { leaveType, hours },
          true
        );
      }

      // 최소 단위 확인
      if (settings?.minimumHourlyUnit && hours < settings.minimumHourlyUnit) {
        throw new AppError(
          `최소 ${settings.minimumHourlyUnit}시간 이상 신청 가능합니다.`,
          ErrorCodes.VALIDATION_ERROR,
          { minimumHourlyUnit: settings.minimumHourlyUnit, requestedHours: hours },
          true
        );
      }

      return hours / 8;

    default:
      throw new AppError(
        `알 수 없는 연차 종류: ${leaveType}`,
        ErrorCodes.VALIDATION_ERROR,
        { leaveType },
        true
      );
  }
};

// ===== 검증 함수 =====

/**
 * 연차 신청 날짜 범위 검증
 *
 * @param startDate 시작일
 * @param endDate 종료일
 * @param balance 연차 잔액 정보
 * @param deductionDays 차감 일수
 * @returns 검증 결과
 */
export const validateDateRange = (
  startDate: Date,
  endDate: Date,
  balance: AnnualLeaveBalance,
  deductionDays: number
): ValidationResult => {
  const normalizedStart = normalizeDate(startDate);
  const normalizedEnd = normalizeDate(endDate);

  // 날짜 순서 확인
  if (normalizedStart > normalizedEnd) {
    return {
      isValid: false,
      error: '시작일이 종료일보다 늦을 수 없습니다.',
    };
  }

  // 유효기간 확인
  if (!isDateInValidPeriod(normalizedStart, balance.validFrom, balance.validUntil) ||
      !isDateInValidPeriod(normalizedEnd, balance.validFrom, balance.validUntil)) {
    return {
      isValid: false,
      error: `연차 사용 가능 기간: ${formatDate(balance.validFrom)} ~ ${formatDate(balance.validUntil)}`,
    };
  }

  // 잔액 확인
  if (deductionDays > balance.remainingDays) {
    return {
      isValid: false,
      error: `잔여 연차(${balance.remainingDays}일) 부족. 필요: ${deductionDays}일`,
    };
  }

  // 경고 사항 확인
  const warnings: string[] = [];

  // 90% 이상 사용 경고
  const usageRate = ((balance.usedDays + deductionDays) / balance.totalDays) * 100;
  if (usageRate >= 90) {
    warnings.push(`연차 사용률이 ${Math.round(usageRate)}%에 도달합니다.`);
  }

  // 유효기간 만료 임박 경고 (30일 이내)
  const daysUntilExpiry = Math.floor(
    (balance.validUntil.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  );
  if (daysUntilExpiry <= 30 && daysUntilExpiry > 0) {
    warnings.push(`유효기간이 ${daysUntilExpiry}일 후 만료됩니다.`);
  }

  return {
    isValid: true,
    warnings: warnings.length > 0 ? warnings : undefined,
  };
};

/**
 * 연차 발생 자격 확인
 *
 * @param params 연차 계산 파라미터
 * @returns 연차 계산 결과
 */
export const checkLeaveEligibility = (
  params: LeaveCalculationParams
): LeaveCalculationResult => {
  const { hireDate, checkDate = new Date(), isLargeWorkplace, settings } = params;

  const seniorityYears = calculateSeniorityYears(hireDate, checkDate);

  // 1년 미만은 월차 대상
  if (seniorityYears < 1) {
    return {
      seniorityYears,
      totalDays: 0,
      validFrom: new Date(),
      validUntil: new Date(),
      isEligible: false,
      reason: '입사 1년 미만으로 월차 대상입니다.',
    };
  }

  // 입사 기념일 계산
  const anniversaryDate = new Date(hireDate);
  anniversaryDate.setFullYear(checkDate.getFullYear());

  // 이미 지난 경우 작년 기념일 사용
  if (anniversaryDate > checkDate) {
    anniversaryDate.setFullYear(anniversaryDate.getFullYear() - 1);
  }

  const { validFrom, validUntil } = calculateValidPeriod(anniversaryDate);
  const totalDays = calculateTotalLeaveDays(seniorityYears, isLargeWorkplace, settings);

  return {
    seniorityYears,
    totalDays,
    validFrom,
    validUntil,
    isEligible: true,
  };
};

// ===== 포맷팅 유틸리티 =====

/**
 * 연차 종류 라벨 반환
 */
export const getLeaveTypeLabel = (leaveType: LeaveType): string => {
  switch (leaveType) {
    case 'full': return '전일';
    case 'half': return '반차';
    case 'hourly': return ''; // 병가 UI 제거 - 빈 문자열 반환
    default: return '';
  }
};

/**
 * 반차 종류 라벨 반환
 */
export const getHalfDayTypeLabel = (halfDayType: HalfDayType): string => {
  switch (halfDayType) {
    case 'morning': return '오전';
    case 'afternoon': return '오후';
    default: return '';
  }
};

/**
 * 신청 상태 라벨 반환
 */
export const getLeaveStatusLabel = (status: string): string => {
  switch (status) {
    case 'pending': return '승인 대기';
    case 'approved': return '승인됨';
    case 'rejected': return '반려됨';
    case 'cancelled': return '취소됨';
    default: return '';
  }
};

/**
 * 남은 일수를 퍼센티지로 변환
 */
export const calculateRemainingPercentage = (
  remainingDays: number,
  totalDays: number
): number => {
  if (totalDays === 0) return 0;
  return Math.round((remainingDays / totalDays) * 100);
};

/**
 * 연차 만료일까지 남은 일수 계산
 *
 * @param validUntil 연차 만료일
 * @returns 남은 일수
 */
export const calculateDaysUntilExpiry = (validUntil: Date): number => {
  const now = new Date();
  const diffTime = validUntil.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};